import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
  useTransitionStyles,
} from "@floating-ui/react";
import React, { cloneElement, isValidElement, useRef, useState } from "react";
import usePortalRoot from "@/shared/hooks/usePortalRoot";

type Placement =
  | "top"
  | "top-start"
  | "top-end"
  | "bottom"
  | "bottom-start"
  | "bottom-end"
  | "left"
  | "left-start"
  | "left-end"
  | "right"
  | "right-start"
  | "right-end";

type TooltipProps = {
  children?: any;
  content?: any;
  placement?: Placement;
  disabled?: boolean;
  maxWidth?: number;
  openDelay?: number;
  closeDelay?: number;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: "hover" | "click";
};

const ARROW_HEIGHT = 8;
const ARROW_WIDTH = 16;
const GAP = 6;

const Tooltip = ({
  children,
  content,
  placement = "top",
  disabled = false,
  maxWidth = 260,
  openDelay = 100,
  closeDelay = 50,
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  trigger = "hover",
}: TooltipProps) => {
  const hasContent = content !== undefined && content !== null;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const portalRoot = usePortalRoot();
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const arrowRef = useRef<SVGSVGElement | null>(null);

  const setOpen = (nextOpen: boolean) => {
    if (!isControlled) {
      setUncontrolledOpen(nextOpen);
    }
    onOpenChange?.(nextOpen);
  };

  const { refs, floatingStyles, context } = useFloating({
    open: disabled ? false : open,
    onOpenChange: setOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(ARROW_HEIGHT + GAP),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      arrow({
        element: arrowRef,
        padding: 8,
      }),
    ],
  });

  const hover = useHover(context, {
    enabled: !disabled && trigger === "hover",
    delay: {
      open: openDelay,
      close: closeDelay,
    },
    move: false,
  });

  const click = useClick(context, {
    enabled: !disabled && trigger === "click",
  });

  const focus = useFocus(context, {
    enabled: !disabled && trigger === "hover",
  });

  const dismiss = useDismiss(context, {
    escapeKey: true,
  });

  const role = useRole(context, {
    role: "tooltip",
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    click,
    focus,
    dismiss,
    role,
  ]);

  const { styles: transitionStyles, isMounted } = useTransitionStyles(context, {
    duration: {
      open: 140,
      close: 100,
    },
    initial: {
      opacity: 0,
      transform: "scale(0.98)",
    },
    open: {
      opacity: 1,
      transform: "scale(1)",
    },
    close: {
      opacity: 0,
      transform: "scale(0.98)",
    },
  });

  const child =
    isValidElement(children) &&
    cloneElement(
      children as React.ReactElement<any>,
      getReferenceProps({
        ref: refs.setReference,
        ...(children as React.ReactElement<any>).props,
      })
    );

  if (!hasContent) return <div>{children}</div>;

  return (
    <>
      {isValidElement(children) ? (
        child
      ) : (
        <span
          ref={refs.setReference}
          className="inline-flex items-center"
          {...getReferenceProps()}
        >
          {children}
        </span>
      )}

      {!disabled && isMounted && (
        <FloatingPortal root={portalRoot}>
          <div
            ref={refs.setFloating}
            className="relative z-10001"
            style={floatingStyles}
            {...getFloatingProps()}
          >
            <div
              className="pointer-events-none rounded-xl border border-[rgba(255,255,255,0.06)] bg-[#111827] px-3 py-2.5 text-[#f9fafb] shadow-[0_14px_38px_rgba(0,0,0,0.22),0_3px_12px_rgba(0,0,0,0.12)] origin-center will-change-[opacity,transform]"
              style={{ ...transitionStyles, maxWidth: `${maxWidth}px` }}
            >
              <FloatingArrow
                ref={arrowRef}
                context={context}
                width={ARROW_WIDTH}
                height={ARROW_HEIGHT}
                tipRadius={2}
                fill="#111827"
                stroke="rgba(255,255,255,0.06)"
                strokeWidth={1}
              />
              <div className="text-[17px] font-medium leading-[1.4] wrap-break-word">
                <div className="text-(length:--screen-timer-font-size)">
                  {content}
                </div>
              </div>
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

export default Tooltip;
